import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useAppInstanceContext } from '../providers/app-instance-provider.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';

type SummaryField = {
  queryKey:
    | 'topicId'
    | 'geographyTypeId'
    | 'contentAreaId'
    | 'indicatorId'
    | 'measureId';
  optionsKey:
    | 'topicNames'
    | 'geographyTypes'
    | 'contentAreas'
    | 'indicators'
    | 'measures';
};

const SUMMARY_FIELDS: SummaryField[] = [
  { queryKey: 'topicId', optionsKey: 'topicNames' },
  { queryKey: 'geographyTypeId', optionsKey: 'geographyTypes' },
  { queryKey: 'contentAreaId', optionsKey: 'contentAreas' },
  { queryKey: 'indicatorId', optionsKey: 'indicators' },
  { queryKey: 'measureId', optionsKey: 'measures' },
];

function normalizeSelectedYearIds(year: unknown): string[] {
  if (Array.isArray(year)) {
    return year.map((value) => String(value));
  }

  if (year) {
    return [String(year)];
  }

  return [];
}

function sortYearIdsAsc(yearIds: string[]): string[] {
  return [...yearIds].sort((a, b) => Number(a) - Number(b));
}

export const HeaderSummary = memo(function HeaderSummary() {
  const { instanceState, setActiveYearId } = useAppInstanceContext();
  const { query, options, advancedFiltersState, activeYearId } = instanceState;

  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const timeoutRef = useRef<number | null>(null);
  const lastSelectedYearKeyRef = useRef('');

  const baseParts = useMemo(() => {
    const nextParts: string[] = [];

    for (const { queryKey, optionsKey } of SUMMARY_FIELDS) {
      const selectedId = query[queryKey];
      if (!selectedId) continue;

      const match = options[optionsKey]?.find(
        (opt) => String(opt.id) === String(selectedId),
      );

      if (match) {
        nextParts.push(match.name);
      }
    }

    return nextParts;
  }, [query, options]);

  const filterParts = useMemo(() => {
    const nextParts: string[] = [];
    const selectedAdvancedFilters = query.advancedFilters ?? {};

    for (const filterDefinition of Object.entries(selectedAdvancedFilters)) {
      const filterDef = advancedFiltersState.find((ad) => ad.code === filterDefinition[0])
      if (!filterDef) continue;
      const effectiveOption = filterDef.options.find(
            (opt) => String(opt.id) === String(filterDefinition[1]),
          );

      if (effectiveOption) {
        nextParts.push(effectiveOption.value);
      }
    }

    return nextParts;
  }, [query.advancedFilters, advancedFiltersState]);

  const selectedYearIds = useMemo(
    () => sortYearIdsAsc(normalizeSelectedYearIds(query.year)),
    [query.year],
  );

  const selectedYearOptions = useMemo(() => {
    const yearOptions = options.years ?? [];

    return yearOptions
      .filter((year) => selectedYearIds.includes(String(year.id)))
      .sort((a, b) => {
        const aNum = Number(a.name ?? a.id);
        const bNum = Number(b.name ?? b.id);

        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
          return aNum - bNum;
        }

        return String(b.name ?? b.id).localeCompare(String(a.name ?? a.id));
      });
  }, [options.years, selectedYearIds]);

  const activeYearLabel = useMemo(() => {
    if (!activeYearId) {
      return selectedYearOptions[0]?.name ?? 'Choose year';
    }

    const match = selectedYearOptions.find(
      (year) => String(year.id) === String(activeYearId),
    );

    return match?.name ?? selectedYearOptions[0]?.name ?? 'Choose year';
  }, [selectedYearOptions, activeYearId]);

  useEffect(() => {
    const selectedYearKey = selectedYearIds.join('|');

    if (selectedYearKey === lastSelectedYearKeyRef.current) {
      return;
    }

    lastSelectedYearKeyRef.current = selectedYearKey;

    if (selectedYearIds.length === 0) {
      setCurrentIndex(0);
      setIsAutoPlaying(false);
      setActiveYearId(null);
      return;
    }

    setCurrentIndex(0);
    setActiveYearId(selectedYearIds[0])
    setIsAutoPlaying(selectedYearIds.length > 1);
  }, [selectedYearIds, setActiveYearId]);

  useEffect(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!isAutoPlaying) return;
    if (selectedYearIds.length <= 1) return;
    if (currentIndex >= selectedYearIds.length - 1) {
      setIsAutoPlaying(false);
      return;
    }

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isAutoPlaying, currentIndex, selectedYearIds, setActiveYearId]);

  function selectYear(yearId: string) {
    const index = selectedYearIds.findIndex((id) => id === yearId);

    if (index >= 0) {
      setCurrentIndex(index);
    }

    setActiveYearId(yearId);
    setIsAutoPlaying(false);
    // No manual close needed — DropdownMenuRadioItem closes the menu
    // on select by default (Radix's own behavior), unlike the
    // previous hand-rolled version which had to track and close open
    // state itself.
  }

  return (
    <div className="flex justify-center items-center gap-1 text-primary">
      {selectedYearOptions.length > 1 && (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger className="header-year-trigger flex items-center gap-1">
              {activeYearLabel}
              <ChevronDown className="size-3.5 opacity-60" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuRadioGroup
                value={String(activeYearId ?? '')}
                onValueChange={selectYear}
              >
                {selectedYearOptions.map((year) => {
                  const yearId = String(year.id);
                  return (
                    <DropdownMenuRadioItem key={yearId} value={yearId}>
                      {year.name}
                    </DropdownMenuRadioItem>
                  );
                })}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {baseParts.length > 0 && <span>|</span>}
        </>
      )}
      {selectedYearIds.length > 0 && selectedYearIds.length === 1 && <span>{activeYearLabel + " | "}</span>}
      {baseParts.length > 0 && <span>{baseParts.join(' | ')}</span>}

      {filterParts.length > 0 && (
        <>
          {(baseParts.length > 0 || selectedYearOptions.length > 0) && <span>|</span>}
          <span>{filterParts.join(' | ')}</span>
        </>
      )}
    </div>
  );
});